import { useEffect, useState } from 'react'
import { authClient } from '../lib/authClient'
import { Button } from '../components/atoms/Button'
import { Input } from '../components/atoms/Input'
import { Spinner } from '../components/atoms/Spinner'

type Step = 'email' | 'code'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const TOO_MANY_REQUESTS = 429
const NO_ACCESS_MESSAGE = 'This account has no access. Use the address from your invitation.'
const PRIVACY_URL = 'https://saysaybar.com/terms'

function hasRedirectError(): boolean {
	return new URLSearchParams(window.location.search).has('error')
}

function describeError(status: number, fallback: string): string {
	return status === TOO_MANY_REQUESTS ? 'Too many attempts. Wait a minute and try again.' : fallback
}

export default function Login({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
	const [step, setStep] = useState<Step>('email')
	const [email, setEmail] = useState('')
	const [code, setCode] = useState('')
	const [error, setError] = useState<string | null>(() => (hasRedirectError() ? NO_ACCESS_MESSAGE : null))
	const [isBusy, setIsBusy] = useState(false)

	useEffect(() => {
		if (hasRedirectError()) window.history.replaceState(null, '', window.location.pathname)
	}, [])

	const isPasskeySupported = typeof window.PublicKeyCredential !== 'undefined'

	async function signInWithGoogle() {
		setIsBusy(true)
		setError(null)
		const { error: failure } = await authClient.signIn.social({
			provider: 'google',
			callbackURL: window.location.origin,
			errorCallbackURL: window.location.origin,
		})
		if (failure) {
			setError(describeError(failure.status, NO_ACCESS_MESSAGE))
			setIsBusy(false)
		}
	}

	async function signInWithPasskey() {
		setIsBusy(true)
		setError(null)
		const { error: failure } = await authClient.signIn.passkey()
		if (failure) {
			setError(describeError(failure.status, 'Face ID login did not work. Use Google or a code instead.'))
			setIsBusy(false)
			return
		}
		await onSignedIn()
	}

	async function sendCode() {
		const normalizedEmail = email.trim().toLowerCase()
		if (!EMAIL_REGEX.test(normalizedEmail)) {
			setError('Please enter a valid email address.')
			return
		}
		setIsBusy(true)
		setError(null)
		const { error: failure } = await authClient.emailOtp.sendVerificationOtp({ email: normalizedEmail, type: 'sign-in' })
		setIsBusy(false)
		if (failure) {
			setError(describeError(failure.status, 'The code could not be sent. Try again.'))
			return
		}
		setEmail(normalizedEmail)
		setStep('code')
	}

	async function verifyCode() {
		setIsBusy(true)
		setError(null)
		const { error: failure } = await authClient.signIn.emailOtp({ email, otp: code.trim() })
		if (failure) {
			setError(describeError(failure.status, 'Wrong or expired code.'))
			setIsBusy(false)
			return
		}
		await onSignedIn()
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-chrome px-4 pt-safe pb-safe">
			<div className="w-full max-w-sm flex flex-col gap-6 bg-card rounded-2xl p-6">
				<div className="flex flex-col gap-1 text-center">
					<span className="text-lg font-medium tracking-wider">SAYSAY</span>
					<span className="text-sm text-muted-foreground">Backoffice</span>
				</div>

				{step === 'email' ? (
					<div className="flex flex-col gap-3">
						<Button variant="primary" fullWidth disabled={isBusy} onClick={signInWithGoogle}>
							<i className="ti ti-brand-google text-base" aria-hidden="true" />
							Continue with Google
						</Button>
						{isPasskeySupported && (
							<div className="flex flex-col gap-1">
								<Button variant="secondary" fullWidth disabled={isBusy} onClick={signInWithPasskey}>
									<i className="ti ti-fingerprint text-base" aria-hidden="true" />
									Face ID or fingerprint
								</Button>
								<span className="text-xs text-muted-foreground text-center">After you set it up once in the app</span>
							</div>
						)}

						<div className="flex items-center gap-3 text-xs text-muted-foreground">
							<span className="flex-1 border-t border-border" />
							or get a code by email
							<span className="flex-1 border-t border-border" />
						</div>

						<Input
							type="email"
							inputMode="email"
							autoComplete="email"
							placeholder="email@example.com"
							value={email}
							onChange={(e) => {
								setEmail(e.target.value)
								setError(null)
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter') sendCode()
							}}
						/>
						<Button variant="secondary" fullWidth disabled={isBusy || !email.trim()} onClick={sendCode}>
							{isBusy ? <Spinner className="size-4" /> : 'Send code'}
						</Button>
					</div>
				) : (
					<div className="flex flex-col gap-3">
						<p className="text-sm text-muted-foreground">
							If <strong className="text-foreground">{email}</strong> has access, a 6-digit code is on its way.
						</p>
						<Input
							inputMode="numeric"
							autoComplete="one-time-code"
							maxLength={6}
							placeholder="123456"
							value={code}
							onChange={(e) => {
								setCode(e.target.value)
								setError(null)
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter') verifyCode()
							}}
						/>
						<Button variant="primary" fullWidth disabled={isBusy || code.trim().length !== 6} onClick={verifyCode}>
							{isBusy ? <Spinner className="size-4" /> : 'Log in'}
						</Button>
						<Button
							variant="ghost"
							fullWidth
							disabled={isBusy}
							onClick={() => {
								setStep('email')
								setCode('')
								setError(null)
							}}
						>
							Use another method
						</Button>
					</div>
				)}

				{error && (
					<p className="text-xs text-error text-center" role="alert">
						{error}
					</p>
				)}

				<a href={PRIVACY_URL} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground text-center hover:text-foreground">
					Privacy
				</a>
			</div>
		</div>
	)
}
